import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { GlobalAccount, GUID } from '@ndvr/customer-api';
import { Account, NoteType } from '@ndvr/portfolio-api';

import { useCreateNote, useNote, useUpdateNote } from 'views/person/note-queries';
import { useToggleAccountTradeStatus } from 'views/portfolio/portfolio-queries';
import { ConfirmModal, ModalText, Text, VerticalLayout } from 'widgets';

export const AccountTradeStatusModal = ({
    householdGUID,
    account,
    onClose,
}: {
    householdGUID: GUID<GlobalAccount>;
    account: Account;
    onClose: () => void;
}) => {
    const { mutate: toggleTradeStatus } = useToggleAccountTradeStatus(householdGUID);

    const { data: note } = useNote(account.accountGUID, NoteType.TRADE_SUSPENSION_REASON);
    const { mutate: createNote } = useCreateNote();
    const { mutate: updateNote } = useUpdateNote();

    const [noteContent, setNoteContent] = useState<string>('');
    useEffect(() => setNoteContent(note?.content.plainText ?? ''), [note?.content.plainText]);

    const isSuspended = !!account.tradingSuspendedAt;

    const handleSaveNote = () => {
        if (!note && noteContent) {
            createNote({
                entityGUID: account.accountGUID,
                entityType: 'Account',
                noteType: NoteType.TRADE_SUSPENSION_REASON,
                content: noteContent,
            });
        }
        if (note && noteContent !== note.content.plainText) {
            updateNote({
                noteGUID: note.noteGUID,
                entityGUID: note.entityGUID,
                noteType: note.noteType,
                content: { ...note.content, plainText: noteContent },
            });
        }
    };

    return (
        <ConfirmModal
            modalTitle={`${isSuspended ? 'Enable' : 'Disable'} trading on account`}
            buttonProps={{
                buttonType: 'destructive',
                size: 'small',
                children: `${isSuspended ? 'Enable' : 'Disable'} trading`,
            }}
            onConfirm={() => {
                toggleTradeStatus(account);
                handleSaveNote();
            }}
            onClose={onClose}
        >
            <VerticalLayout $gap={16}>
                <ModalText>
                    Are you sure you want to {isSuspended ? 'enable' : 'disable'} trading on{' '}
                    {account.accountName.plainText}?
                </ModalText>
                <VerticalLayout $gap={4}>
                    <Text $themeColor="subtle">Note:</Text>
                    <TextArea value={noteContent} onChange={(event) => setNoteContent(event.target.value)} />
                </VerticalLayout>
            </VerticalLayout>
        </ConfirmModal>
    );
};

const TextArea = styled.textarea`
    height: 120px;
    padding: 8px;
    border: 1px solid ${({ theme }) => theme.border.dark};
    color: ${({ theme }) => theme.text.primary};
    font-family: halyard-text, sans-serif;
    font-size: 1rem;
    resize: none;
`;
